<template>
  <div :key="product.globalId">
    <div class="detail">
      <ODFLayout
        class="detail__left"
        :title="$store.state.config.common.brand"
        :link="'/'"
        :theme="parent.theme"
        :blason="parent.intro.blason"
        :pattern="parent.intro.pattern"
      >
        <div
          slot="header"
          class="detail__nav"
        >
          <TextNav :items="navItems" />
        </div>

        <div class="detail__content">
          <div class="detail__centered">
            <Viewer
              class="detail__viewer"
              :theme="parent.theme"
              :material="coinMaterial"
              :normals="coinNormals"
            />
          </div>
        </div>

        <ButtonVideo
          v-if="buttonVideoConfig.available"
          slot="footer"
          v-bind="buttonVideoConfig.props"
        />
      </ODFLayout>

      <div
        v-if="product"
        class="detail__scroller-container"
      >
        <div class="detail__scroller">
          <div class="detail__card-container">
            <ProductCard
              class="detail__card"
              :theme="parent.theme"
              :product="product"
              :parent="parent"
            />
          </div>

          <div
            v-if="giftEnabled && product.gift"
            class="detail__gift-container"
          >
            <Gift
              class="detail__gift"
              :theme="parent.theme"
            />
          </div>

          <div class="detail__more-container">
            <ProductMore
              :title="parent.collection.discoverMore"
              :theme="parent.theme"
              :products="products"
              :ignore-id="product.id.replace(parent.year + '-', '')"
              class="detail__more"
            />
          </div>
        </div>
      </div>
    </div>

    <ButtonVideoMobile
      v-if="buttonVideoConfig.available"
      v-bind="buttonVideoConfig.props"
      :theme="parent.theme"
      class="detail__button-video"
    />

    <MobileFooterNav
      :config="parent"
      class="detail__footer-navigation"
    />
  </div>
</template>

<script>
import Viewer from '@/components/viewer/Viewer.vue';
import ButtonVideo from '@/components/buttons/ButtonVideo.vue';
import ProductCard from '@/components/items/ProductCard.vue';
import ProductMore from '@/components/items/ProductMore.vue';
import ODFLayout from '@/components/ODFLayout.vue';
import TextNav from '@/components/buttons/TextNav.vue';
import Gift from '@/components/Gift.vue';
import MobileFooterNav from '@/components/navigation/MobileFooterNav.vue';
import ButtonVideoMobile from '@/components/buttons/ButtonVideoMobile.vue';

export default {
  components: {
    ButtonVideo,
    Viewer,
    ProductCard,
    ProductMore,
    ODFLayout,
    TextNav,
    Gift,
    MobileFooterNav,
    ButtonVideoMobile,
  },

  computed: {
    routeName() {
      return this.$route.name;
    },

    giftEnabled() {
      return window.___gift___enabled && (window.___gift___enabled === true || window.___gift___enabled === 'true');
    },

    product() {
      return this.$store.getters.getDataById(this.routeName);
    },

    parent() {
      const parent = this.$store.getters.getParentData(this.routeName);
      if (parent.is && parent.is === 'Products') {
        return parent;
      }
      return null;
    },

    navItems() {
      const routes = this.$store.getters.getCollectionRoutes();
      const filteredRoutes = routes.filter(route => route.available);

      return filteredRoutes.map(route =>
        ({
          label: route.name,
          href: route.path,
        }));
    },

    products() {
      return [...this.parent.products].reverse();
    },

    coinMaterial() {
      if (!this.product || !this.product.path) return null;
      return this.product.path.startsWith('/gold') ? 'gold' : 'silver';
    },

    coinNormals() {
      if (!this.product || !this.product.path) return null;
      return `${this.$store.state.staticAssetsPath}normalmaps/${this.parent.id}${this.product.path}.jpeg`;
    },

    buttonVideoConfig() {
      return this.$store.getters.getButtonVideoConfigByConfig(this.parent);
    },
  },
};
</script>

<style lang="stylus" scoped>
.detail {
  &--theme-green { background-color: $cl-green; }
  &--theme-purple { background-color: $cl-purple; }
  &--theme-orange { background-color: $cl-orange; }

  +product-splitscreen() {
    display: flex;
    flex-direction: row;
    width: 100%;
  }
}

.detail__left {
  flex-grow: 1;
  overflow: hidden;
  padding-bottom: 0;

  +product-splitscreen() {
    position: sticky;
    top: 0;

    height: 100vh;
    min-height: 600px;
  }

  +desktop() {
    padding-bottom: 30px;
  }
}

.detail >>> .odf-layout__background {
  height: 75%;
  +product-splitscreen() {
    height: 100%;
  }
}

.detail >>> .odf-layout__title {
  +product-splitscreen() {
    text-align: left;
  }
}

.detail__content {
  +product-splitscreen() {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
}

.detail__centered {
  position: relative;
  z-index: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  padding-bottom: 100%;
  width: 100%;
  height: 0;

  +product-splitscreen() {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    padding: 0;
    height: 100%;
  }
}

.detail__nav {
  display: none;
  margin: 10px 0;

  +product-splitscreen() {
    display: block;
  }
}

.detail__viewer {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;

  width: 100%;
  height: @width;

  +product-splitscreen() {
    position: static;
    flex-shrink: 0;
    width: round(390px * 1.1);
    height: @width;
  }

  +above('xl') {
    width: round(456px * 1.1);
    height: @width;
    max-width:85vh
    max-height:85vh
  }

  +above('xxl') {
    width: round(576px * 1.1);
    height: @width;
    max-width:85vh
    max-height:85vh
  }
}

.detail__scroller-container {
  flex-shrink: 0;
  flex-grow: 0;
  width: 100%;

  +product-splitscreen() { width: 320px; }
  +above('xl') { width: 460px; }
  +above('xxl') { width: 640px; }
}

.detail__scroller {
  box-sizing: border-box;
  width: 100%;
  background: s('linear-gradient(white 1px, %s 2px)', $cl-gold);

  +product-splitscreen() {
    min-height: 100%;
  }
}

.detail__card-container {
  box-sizing: border-box;
  width: 100%;
  padding: 0 20px 30px 20px;

  background-color: $cl-white;

  +desktop() {
    padding: 40px 20px;
  }
}

.detail__card {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.detail__more-container {
  box-sizing: border-box;
  padding: 40px 0px;
  background-color: $cl-gold;

  +product-splitscreen() { padding: 40px 20px; }
  +above('xxl') { padding: 40px 47px; }
}

.detail__gift-container {
  background: linear-gradient(white 50%, $cl-gold 50.5%);
  padding: 0 20px;

  +product-splitscreen() {
    padding: 0;
  }
}

.detail__gift {
  box-sizing: border-box;
  max-width: 600px;
  margin: 0 auto;

  +product-splitscreen() {
    margin: 0;
    width: calc(100% - 20px);
  }
}

.detail__footer-navigation {
  position: sticky;
  bottom: 0;
  z-index: 1;

  +desktop() { display: none; }
}

.detail__button-video {
  +desktop() { display: none; }
}
</style>
